/**
 * WhatsApp Sync Context
 * 
 * Manages WhatsApp background sync state with localStorage persistence.
 * Integrates with GlobalSyncStatusBar to show sync progress.
 * 
 * Features:
 * - Persists pending syncs to localStorage (survives refresh)
 * - Background polling for chat availability
 * - Exposes state to GlobalSyncStatusBar for unified UX
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';

// Use the secure proxy edge function instead of calling Baileys directly
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const STORAGE_KEY = 'n71-whatsapp-sync';
const POLL_INTERVAL_MS = 5000; // 5 seconds
const MAX_POLL_DURATION_MS = 2 * 60 * 1000; // Stop polling after 2 minutes — show what we have
const MIN_NAMED_CONTACTS_RATIO = 0.3; // At least 30% of contacts should have names (was 50% — too strict for large contact lists)
const MIN_CHATS_FOR_READY = 3; // Need at least 3 chats before checking names

/**
 * Check if a chat has a proper name (not just a phone number)
 */
function chatHasProperName(chat: { jid: string; name: string; isGroup: boolean }): boolean {
  if (chat.isGroup) return true; // Groups always have names
  
  const phone = chat.jid.replace('@s.whatsapp.net', '').replace('@g.us', '');
  const name = chat.name || '';
  
  // Name is proper if it's not empty and not just the phone number
  return name.length > 0 && name !== phone && name !== chat.jid;
}

/**
 * Check if enough contacts have synced their names
 */
function hasEnoughNamedContacts(chats: Array<{ jid: string; name: string; isGroup: boolean }>): boolean {
  if (chats.length < MIN_CHATS_FOR_READY) return false;
  
  // Filter to non-group chats only
  const contacts = chats.filter(c => !c.isGroup);
  if (contacts.length === 0) return true; // All groups is fine
  
  // Count how many have proper names
  const namedCount = contacts.filter(chatHasProperName).length;
  const ratio = namedCount / contacts.length;
  
  console.log(`[WhatsAppSync] Contact name check: ${namedCount}/${contacts.length} have names (${Math.round(ratio * 100)}%)`);
  
  return ratio >= MIN_NAMED_CONTACTS_RATIO;
}

export interface SyncProgress {
  current: number;
  total: number;
  chatName: string;
}

/**
 * Banner dismissal state.
 *   undefined       — banner is eligible to show (user hasn't dismissed)
 *   ISO date string — snoozed until this time (then revert to eligible)
 *   'opted_out'     — user explicitly chose "Don't ask again", never show
 */
export type BannerDismissal = string | 'opted_out' | undefined;

export interface PendingWhatsAppSync {
  sessionId: string;
  workspaceId: string;
  startedAt: string;
  chatsReady: boolean;
  chatCount?: number;
  // Active sync progress (when user has selected chats and sync is running)
  isSyncing?: boolean;
  syncProgress?: SyncProgress;
  // Chat-picker banner dismissal state (see BannerDismissal type above).
  // Absence = eligible to show once chatsReady. Completing the chat selection
  // removes the whole sync entry, so there's no "completed" state here.
  bannerDismissedUntil?: BannerDismissal;
}

interface WhatsAppSyncContextValue {
  // State
  pendingSyncs: PendingWhatsAppSync[];
  chatSelectorOpen: boolean;
  activeSyncSessionId: string | null;

  // Actions
  addPendingSync: (sessionId: string, workspaceId: string) => void;
  removePendingSync: (sessionId: string) => void;
  openChatSelector: (sessionId: string) => void;
  closeChatSelector: () => void;
  startBackgroundSync: (sessionId: string) => void;
  updateSyncProgress: (sessionId: string, progress: SyncProgress) => void;
  completeSyncWithSuccess: (sessionId: string, messageCount: number, chatCount: number) => void;
  completeSyncWithError: (sessionId: string) => void;
  // Chat-picker banner dismissal actions
  snoozeBanner: (sessionId: string, days?: number) => void;
  optOutBanner: (sessionId: string) => void;

  // Derived
  hasPendingSyncs: boolean;
  hasReadySyncs: boolean;
  hasActiveSyncs: boolean;
  /**
   * The sync that should currently surface the chat-picker banner, or null.
   * Filters pendingSyncs for: chatsReady && !isSyncing && dismissal state allows showing.
   * Used by WhatsAppChatPickerBanner mounted in App.tsx.
   */
  bannerEligibleSync: PendingWhatsAppSync | null;
}

const WhatsAppSyncContext = createContext<WhatsAppSyncContextValue | null>(null);

// Load from localStorage
function loadFromStorage(): PendingWhatsAppSync[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Filter out stale syncs (older than 24 hours)
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      return parsed.filter((s: PendingWhatsAppSync) => 
        new Date(s.startedAt).getTime() > dayAgo
      );
    }
  } catch (err) {
    console.error('[WhatsAppSync] Failed to load from storage:', err);
  }
  return [];
}

// Save to localStorage
function saveToStorage(syncs: PendingWhatsAppSync[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(syncs));
  } catch (err) {
    console.error('[WhatsAppSync] Failed to save to storage:', err);
  }
}

export function WhatsAppSyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [pendingSyncs, setPendingSyncs] = useState<PendingWhatsAppSync[]>(() => loadFromStorage());
  const [chatSelectorOpen, setChatSelectorOpen] = useState(false);
  const [activeSyncSessionId, setActiveSyncSessionId] = useState<string | null>(null);
  const pollingRef = useRef<boolean>(false);

  // Persist to localStorage on changes
  useEffect(() => {
    saveToStorage(pendingSyncs);
  }, [pendingSyncs]);

  // Hydrate from server — seeds pendingSyncs for any orphaned WhatsApp
  // integrations on this workspace where we never completed the chat
  // picker flow. This catches the Kareem failure mode: user scanned QR
  // before the picker banner shipped, so their localStorage has no
  // entry, so the banner never appears to nudge them. Instead of
  // waiting for fresh localStorage, we infer orphans from the DB:
  //
  //   provider='whatsapp_personal'
  //   AND is_active=true
  //   AND last_incremental_at IS NULL   (never ingested a message)
  //   AND custom_config IS NULL          (never stored a chat selection)
  //
  // For each match, we synthesize a pendingSync with sessionId in the
  // bridge's canonical `{workspace_id}_{user_id}` format. The existing
  // poll loop then asks the bridge whether chats are ready; if the
  // session doesn't exist on the bridge the poll just quietly fails
  // and the banner stays hidden. No UI noise from false positives.
  useEffect(() => {
    if (!user?.id || !currentWorkspace?.id) return;

    let cancelled = false;
    const workspaceId = currentWorkspace.id;
    const userId = user.id;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('integration_accounts')
          .select('id, workspace_id, connected_at')
          .eq('workspace_id', workspaceId)
          .eq('provider', 'whatsapp_personal')
          .eq('is_active', true)
          .is('last_incremental_at', null)
          .is('custom_config', null);

        if (cancelled) return;
        if (error) {
          console.warn('[WhatsAppSync] Hydration query failed:', error.message);
          return;
        }
        if (!data || data.length === 0) return;

        setPendingSyncs(prev => {
          const existingIds = new Set(prev.map(s => s.sessionId));
          const additions: PendingWhatsAppSync[] = [];

          for (const row of data) {
            const sessionId = `${row.workspace_id}_${userId}`;
            if (existingIds.has(sessionId)) continue;
            additions.push({
              sessionId,
              workspaceId: row.workspace_id,
              // Use the integration's connect time rather than "now" so the
              // stale-filter in loadFromStorage doesn't immediately cull a
              // synthetic entry for a week-old connection.
              startedAt: new Date().toISOString(),
              chatsReady: false,
            });
          }

          if (additions.length === 0) return prev;
          console.log(
            `[WhatsAppSync] Hydrated ${additions.length} orphan sync(s) from integration_accounts`,
          );
          return [...prev, ...additions];
        });
      } catch (err) {
        console.warn('[WhatsAppSync] Hydration error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, currentWorkspace?.id]);

  // Background polling for chat availability
  useEffect(() => {
    const pendingNotReady = pendingSyncs.filter(s => !s.chatsReady);
    
    if (pendingNotReady.length === 0) {
      pollingRef.current = false;
      return;
    }

    pollingRef.current = true;
    const pollStartTime = Date.now();

    const poll = async () => {
      if (!pollingRef.current) return;

      for (const sync of pendingNotReady) {
        try {
          // Get current session for auth
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) continue;

          // Call secure proxy (authenticates with JWT, adds bridge secret server-side)
          const response = await fetch(
            `${SUPABASE_URL}/functions/v1/whatsapp-bridge-proxy/sessions/${sync.sessionId}/chats`,
            {
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              },
            }
          );
          
          if (!response.ok) continue;
          
          const data = await response.json();
          
          if (data.success && data.chats?.length > 0) {
            const elapsed = Date.now() - pollStartTime;
            const timedOut = elapsed > MAX_POLL_DURATION_MS;

            // Check if enough contacts have synced their names
            // This prevents showing the chat selector before names are available
            if (!timedOut && !hasEnoughNamedContacts(data.chats)) {
              console.log(`[WhatsAppSync] Waiting for contact names to sync (${data.chats.length} chats, ${Math.round(elapsed / 1000)}s elapsed)`);
              continue; // Keep polling until names are ready or timeout
            }

            if (timedOut) {
              console.log(`[WhatsAppSync] Timed out waiting for names after ${Math.round(elapsed / 1000)}s — showing ${data.chats.length} chats as-is`);
            }
            
            // Chats are ready (with names or timed out)!
            setPendingSyncs(prev => prev.map(s =>
              s.sessionId === sync.sessionId
                ? { ...s, chatsReady: true, chatCount: data.chats.length }
                : s
            ));
            
            console.log(`[WhatsAppSync] Chats ready for ${sync.sessionId}: ${data.chats.length} chats`);
          }
        } catch (err) {
          console.error('[WhatsAppSync] Poll error:', err);
        }
      }
    };

    // Initial poll
    poll();

    // Set up interval
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      pollingRef.current = false;
      clearInterval(interval);
    };
  }, [pendingSyncs]);

  // Actions
  const addPendingSync = useCallback((sessionId: string, workspaceId: string) => {
    setPendingSyncs(prev => {
      // Remove existing sync for same session if any
      const filtered = prev.filter(s => s.sessionId !== sessionId);
      return [
        ...filtered,
        {
          sessionId,
          workspaceId,
          startedAt: new Date().toISOString(),
          chatsReady: false,
        },
      ];
    });
  }, []);

  const removePendingSync = useCallback((sessionId: string) => {
    setPendingSyncs(prev => prev.filter(s => s.sessionId !== sessionId));
  }, []);

  const openChatSelector = useCallback((sessionId: string) => {
    setActiveSyncSessionId(sessionId);
    setChatSelectorOpen(true);
  }, []);

  const closeChatSelector = useCallback(() => {
    setActiveSyncSessionId(null);
    setChatSelectorOpen(false);
  }, []);

  // Start background sync (user selected chats, sync is now running)
  const startBackgroundSync = useCallback((sessionId: string) => {
    setPendingSyncs(prev => prev.map(s =>
      s.sessionId === sessionId
        ? { ...s, isSyncing: true, syncProgress: { current: 0, total: 0, chatName: 'Starting...' } }
        : s
    ));
  }, []);

  // Update sync progress
  const updateSyncProgress = useCallback((sessionId: string, progress: SyncProgress) => {
    setPendingSyncs(prev => prev.map(s =>
      s.sessionId === sessionId
        ? { ...s, syncProgress: progress }
        : s
    ));
  }, []);

  // Complete sync successfully
  const completeSyncWithSuccess = useCallback((sessionId: string, messageCount: number, chatCount: number) => {
    toast.success(`Synced ${messageCount} messages from ${chatCount} chats`);
    // Remove the sync entry after a brief delay
    setTimeout(() => {
      setPendingSyncs(prev => prev.filter(s => s.sessionId !== sessionId));
    }, 2000);
  }, []);

  // Complete sync with error
  const completeSyncWithError = useCallback((sessionId: string) => {
    setPendingSyncs(prev => prev.map(s =>
      s.sessionId === sessionId
        ? { ...s, isSyncing: false, syncProgress: undefined }
        : s
    ));
  }, []);

  // Snooze the chat-picker banner for N days (default 7). After the snooze
  // window expires, the banner becomes eligible again on next render.
  const snoozeBanner = useCallback((sessionId: string, days: number = 7) => {
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setPendingSyncs(prev => prev.map(s =>
      s.sessionId === sessionId
        ? { ...s, bannerDismissedUntil: until }
        : s
    ));
  }, []);

  // Permanently opt out of the chat-picker banner for this session.
  // The user can still reach the chat picker via /integrations → "Select chats →".
  const optOutBanner = useCallback((sessionId: string) => {
    setPendingSyncs(prev => prev.map(s =>
      s.sessionId === sessionId
        ? { ...s, bannerDismissedUntil: 'opted_out' as const }
        : s
    ));
  }, []);

  // Derived state
  const hasPendingSyncs = pendingSyncs.length > 0;
  const hasReadySyncs = pendingSyncs.some(s => s.chatsReady);
  const hasActiveSyncs = pendingSyncs.some(s => s.isSyncing);

  // The first sync that's currently eligible to show the chat-picker banner.
  // Eligible = chatsReady (chats are loaded) AND !isSyncing (we're not mid-backfill)
  // AND not opted-out AND not snoozed (or snooze has expired).
  const bannerEligibleSync = (() => {
    const now = Date.now();
    return pendingSyncs.find(s => {
      if (!s.chatsReady || s.isSyncing) return false;
      const dismissal = s.bannerDismissedUntil;
      if (dismissal === 'opted_out') return false;
      if (typeof dismissal === 'string') {
        // It's a snooze timestamp — eligible once the snooze has expired
        return new Date(dismissal).getTime() <= now;
      }
      return true; // undefined dismissal = eligible
    }) ?? null;
  })();

  return (
    <WhatsAppSyncContext.Provider
      value={{
        pendingSyncs,
        chatSelectorOpen,
        activeSyncSessionId,
        addPendingSync,
        removePendingSync,
        openChatSelector,
        closeChatSelector,
        startBackgroundSync,
        updateSyncProgress,
        completeSyncWithSuccess,
        completeSyncWithError,
        snoozeBanner,
        optOutBanner,
        hasPendingSyncs,
        hasReadySyncs,
        hasActiveSyncs,
        bannerEligibleSync,
      }}
    >
      {children}
    </WhatsAppSyncContext.Provider>
  );
}

export function useWhatsAppSync() {
  const context = useContext(WhatsAppSyncContext);
  if (!context) {
    throw new Error('useWhatsAppSync must be used within WhatsAppSyncProvider');
  }
  return context;
}
